import { useTranslation } from '@govard/i18n-core/react';
// Страница приёма приглашения по ссылке из письма.
//
// Живёт вне AuthGuard: по ссылке приходят и те, у кого аккаунта ещё нет, и
// показать им «куда зовут» нужно до входа. Сам приём требует входа —
// приглашение привязано к адресу, и проверить это можно только у
// аутентифицированного пользователя.
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Spin, Typography } from 'antd';

import { ApiError } from '../../../shared/api-client/index.js';
import { useCurrentUser, useScopeStore } from '../../../shared/scope/useCurrentScope.js';
import { acceptInvitation, previewInvitation, type InvitationPreview } from '../api/team.client.js';
import { LanguageSwitcher } from '../../../components/layout/LanguageSwitcher.js';

const { Title, Text, Paragraph } = Typography;

export function AcceptInvitePage(): ReactElement {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const fetchMe = useScopeStore((state) => state.fetchMe);

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loadError, setLoadError] = useState<'not_found' | 'load_error' | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    previewInvitation(token)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Store the kind, not a translated string: a locale switch re-renders
        // the alert in the new language instead of keeping the old one.
        setLoadError(err instanceof ApiError && err.status === 404 ? 'not_found' : 'load_error');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleAccept(): Promise<void> {
    if (!token) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      await acceptInvitation(token);
      // Членство изменилось — сессии нужен свежий scope, иначе интерфейс
      // остался бы в прежнем пространстве.
      await fetchMe();
      setAccepted(true);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setAcceptError(
        err instanceof ApiError && err.code === 'forbidden'
          ? t('crm.invite.wrong_email')
          : err instanceof Error
            ? err.message
            : t('crm.invite.accept_error'),
      );
    } finally {
      setAccepting(false);
    }
  }

  const body = (): ReactElement => {
    if (loadError) {
      return (
        <Alert
          type="error"
          showIcon
          title={t('crm.invite.unavailable')}
          description={t(
            loadError === 'not_found' ? 'crm.invite.not_found' : 'crm.invite.load_error',
          )}
        />
      );
    }
    if (!preview) {
      return (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      );
    }
    if (!preview.valid) {
      return (
        <Alert
          type="warning"
          showIcon
          title={t('crm.invite.invalid')}
          description={t(`crm.invite.reason_${preview.reason ?? 'unknown'}`)}
        />
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <Paragraph style={{ margin: 0 }}>
          {t('crm.invite.prefix')} <Text strong>{preview.workspaceName}</Text>{' '}
          {t('crm.invite.as_role', { role: t(`crm.invite.role_${preview.role}`) })}{' '}
          {t('crm.invite.for_email')} <Text code>{preview.email}</Text>.
        </Paragraph>

        {acceptError ? <Alert type="error" showIcon title={acceptError} /> : null}

        {user === null ? (
          <div className="flex flex-col gap-2">
            <Text type="secondary">{t('crm.invite.sign_in_description')}</Text>
            <Link to="/login">
              <Button type="primary">{t('crm.invite.sign_in')}</Button>
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Text type="secondary">{t('crm.invite.signed_in_as', { email: user.email })}</Text>
            <Button
              type="primary"
              loading={accepting}
              disabled={accepted}
              onClick={() => void handleAccept()}
            >
              {t('crm.invite.accept')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <main
      className="relative flex min-h-screen items-center justify-center p-6"
      style={{ backgroundColor: 'var(--ant-color-bg-layout)' }}
    >
      <div className="absolute right-6 top-6" data-testid="public-language-switcher-position">
        <LanguageSwitcher />
      </div>
      <Card style={{ maxWidth: 440, width: '100%' }}>
        <Title level={4} style={{ marginTop: 0 }}>
          {t('crm.invite.title')}
        </Title>
        {body()}
      </Card>
    </main>
  );
}
